import React, { useState } from 'react';
import { useFeedStockAnalytics } from '../hooks/useFeedStockAnalytics';
import { useFormatting } from '../hooks/useFormatting';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  ClockIcon,
  BoltIcon,
  CalendarIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';

interface PeriodSelectorProps {
  value: string;
  onChange: (period: string) => void;
}

const PeriodSelector: React.FC<PeriodSelectorProps> = ({ value, onChange }) => {
  const periods = [
    { value: '7', label: '7 Days' },
    { value: '30', label: '30 Days' },
    { value: '90', label: '90 Days' },
    { value: '365', label: '1 Year' },
    { value: 'all', label: 'All Time' }
  ];

  return (
    <div className="flex space-x-2">
      {periods.map((period) => (
        <button
          key={period.value}
          onClick={() => onChange(period.value)}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            value === period.value
              ? 'bg-cyan-600 text-white'
              : 'bg-gray-100 text-slate-700 hover:bg-gray-200'
          }`}
        >
          {period.label}
        </button>
      ))}
    </div>
  );
};

const FeedStockTrendAnalytics: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('30');
  const { trendAnalytics, trendAnalyticsLoading, trendAnalyticsError, fetchTrendAnalytics } = useFeedStockAnalytics();
  const { formatCurrency, formatDate } = useFormatting();

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    if (period === 'all') {
      fetchTrendAnalytics();
    } else {
      const days = parseInt(period);
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      fetchTrendAnalytics(startDate, endDate);
    }
  };

  if (trendAnalyticsLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (trendAnalyticsError) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-slate-900">Error Loading Trend Analytics</h3>
          <p className="mt-1 text-xs text-slate-500">{trendAnalyticsError}</p>
          <button
            onClick={() => fetchTrendAnalytics()}
            className="mt-4 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!trendAnalytics) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center text-slate-500">
          <ChartBarIcon className="mx-auto h-12 w-12 text-slate-400" />
          <p className="mt-2 text-sm">No trend analytics data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 flex items-center">
              <ChartBarIcon className="h-5 w-5 mr-2" />
              Feed Stock Trend Analytics
            </h2>
            <p className="text-sm text-slate-600">
              Generated {formatDate(new Date(trendAnalytics.generatedAt))} • {trendAnalytics.period}
            </p>
          </div>
          <PeriodSelector value={selectedPeriod} onChange={handlePeriodChange} />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ChartBarIcon className="h-8 w-8 text-cyan-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-slate-500">Items Analyzed</p>
              <p className="text-2xl font-bold text-slate-900">{trendAnalytics.summary.totalItemsAnalyzed || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ArrowTrendingUpIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-slate-500">Trending Up</p>
              <p className="text-2xl font-bold text-green-600">{trendAnalytics.summary.trendingUp || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ArrowTrendingDownIcon className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-slate-500">Trending Down</p>
              <p className="text-2xl font-bold text-red-600">{trendAnalytics.summary.trendingDown || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BoltIcon className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-slate-500">High Volatility</p>
              <p className="text-2xl font-bold text-yellow-600">{trendAnalytics.summary.highVolatility || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-slate-500">Stockout Risk</p>
              <p className="text-2xl font-bold text-red-600">{trendAnalytics.summary.stockoutRisk || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts Section */}
      {trendAnalytics.alerts && trendAnalytics.alerts.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            Trend Alerts & Recommendations
          </h3>
          <div className="space-y-3">
            {trendAnalytics.alerts.slice(0, 5).map((alert, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border-l-4 ${
                  alert.severity === 'high'
                    ? 'bg-red-50 border-red-400'
                    : alert.severity === 'medium'
                    ? 'bg-yellow-50 border-yellow-400'
                    : 'bg-cyan-50 border-blue-400'
                }`}
              >
                <div className="flex">
                  <div className="flex-shrink-0">
                    {alert.severity === 'high' ? (
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                    ) : alert.severity === 'medium' ? (
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                    ) : (
                      <LightBulbIcon className="h-5 w-5 text-blue-400" />
                    )}
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-slate-900">{alert.message}</h4>
                    <p className="text-sm text-slate-600 mt-1">{alert.recommendation}</p>
                    {alert.itemName && (
                      <p className="text-xs text-slate-500 mt-1">Item: {alert.itemName}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Performance Metrics */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-slate-900 mb-4">Performance Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="text-center">
            <p className="text-sm font-medium text-slate-500">Inventory Efficiency</p>
            <p className="text-2xl font-bold text-cyan-600">{trendAnalytics.performanceMetrics.inventoryEfficiency || 0}%</p>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-slate-500">Cost Efficiency</p>
            <p className="text-2xl font-bold text-green-600">{trendAnalytics.performanceMetrics.costEfficiency || 0}%</p>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-slate-500">Forecast Accuracy</p>
            <p className="text-2xl font-bold text-purple-600">{trendAnalytics.performanceMetrics.forecastAccuracy || 0}%</p>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-slate-500">Total Value</p>
            <p className="text-2xl font-bold text-slate-900">
              {formatCurrency(trendAnalytics.performanceMetrics.totalInventoryValue || 0)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-slate-500">Avg Turnover</p>
            <p className="text-2xl font-bold text-cyan-600">
              {(trendAnalytics.performanceMetrics.averageTurnover || 0).toFixed(2)}x
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-slate-500">Optimization Score</p>
            <p className="text-2xl font-bold text-emerald-600">{trendAnalytics.performanceMetrics.optimizationScore || 0}%</p>
          </div>
        </div>
      </div>

      {/* Consumption Trends */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-slate-900 mb-4">Top Consumption Trends</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-slate-50/80">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Item
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Current Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Trend
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Risk Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Days to Stockout
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(trendAnalytics.consumptionTrends || []).slice(0, 10).map((trend) => (
                <tr key={trend.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-slate-900">{trend.name}</div>
                      <div className="text-sm text-slate-500">{trend.type}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {(trend.currentUsage || 0).toFixed(2)}/day
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {trend.trendDirection === 'increasing' ? (
                        <ArrowTrendingUpIcon className="h-4 w-4 text-green-500 mr-1" />
                      ) : trend.trendDirection === 'decreasing' ? (
                        <ArrowTrendingDownIcon className="h-4 w-4 text-red-500 mr-1" />
                      ) : (
                        <div className="h-4 w-4 bg-gray-400 rounded-full mr-1"></div>
                      )}
                      <span className="text-sm text-slate-900 capitalize">{trend.trendDirection}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      trend.riskLevel === 'high'
                        ? 'bg-rose-100 text-rose-800'
                        : trend.riskLevel === 'medium'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-emerald-100 text-emerald-800'
                    }`}>
                      {trend.riskLevel}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {trend.daysToStockout ? `${trend.daysToStockout} days` : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Seasonal Patterns */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center">
          <CalendarIcon className="h-5 w-5 mr-2" />
          Seasonal Patterns
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-3">Seasonal Distribution</h4>
            <div className="space-y-2">
              {Object.entries(trendAnalytics.seasonalPatterns?.seasonal || {}).map(([season, value]) => (
                <div key={season} className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 capitalize">{season}</span>
                  <span className="text-sm font-medium text-slate-900">{formatCurrency(value || 0)}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 text-sm text-slate-600">
              Peak Season: <span className="font-medium text-slate-900 capitalize">{trendAnalytics.seasonalPatterns?.peakSeason || 'Unknown'}</span>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-3">Monthly Usage Pattern</h4>
            <div className="grid grid-cols-3 gap-2">
              {(trendAnalytics.seasonalPatterns?.monthly || []).slice(0, 12).map((month) => (
                <div key={month.month} className="text-center p-2 bg-slate-50/80 rounded">
                  <div className="text-xs text-slate-500">{month.monthName.slice(0, 3)}</div>
                  <div className="text-sm font-medium text-slate-900">{month.percentage}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Forecasting */}
      {trendAnalytics.forecasting && trendAnalytics.forecasting.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center">
            <ClockIcon className="h-5 w-5 mr-2" />
            Forecasting & Recommendations
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Item
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Next Restock Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Optimum Order Qty
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    30-Day Forecast
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(trendAnalytics.forecasting || []).slice(0, 10).map((forecast) => (
                  <tr key={forecast.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-slate-900">{forecast.name}</div>
                        <div className="text-sm text-slate-500">{forecast.type}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      {formatDate(new Date(forecast.nextRestockDate))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      {forecast.optimumOrderQuantity}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">
                        {(forecast.forecasts[0]?.projectedQuantity || 0).toFixed(1)} units
                      </div>
                      <div className="text-xs text-slate-500">
                        {Math.round((forecast.forecasts[0]?.confidence || 0) * 100)}% confidence
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedStockTrendAnalytics; 